import { useCallback, useState } from "react";
import { useRecoilValue } from "recoil";
import { GoMakeDeleteModal } from "@/components";
import { ModalType } from "@/enums";
import { useGomakeAxios, useGomakeRouter, useSnackBar } from "@/hooks";
import {
  getIfCartExistApi,
  updateDocumentApi,
} from "@/services/api-service/generic-doc/documents-api";
import { userQuoteState } from "@/store/user-qoute-state";
import { useTranslation } from "react-i18next";
import {
  decideQuoteOpen,
  type CartQuote,
} from "../utils/quote-open-decision";

/**
 * Open a quote the way the quote table does.
 * Cart present: ask to change status, then save the cart quote as Open and
 * put the clicked quote into the cart. No cart: open the clicked quote.
 */
export function useOpenQuoteLikeList() {
  const { t } = useTranslation();
  const { callApi } = useGomakeAxios();
  const { navigate } = useGomakeRouter();
  const { alertFault, alertFaultUpdate } = useSnackBar();
  const userQuoteIfExist = useRecoilValue<boolean>(userQuoteState);
  const [openModal, setOpenModal] = useState(false);
  const [pendingQuoteId, setPendingQuoteId] = useState<string | null>(null);

  const applyOpen = useCallback(
    async (quoteId: string) => {
      const callBack = (res) => {
        if (res?.success) {
          navigate("/quote");
          return;
        }
        if (res?.statusCode === 85) {
          alertFault(res?.message);
          return;
        }
        alertFaultUpdate();
      };
      await updateDocumentApi(callApi, callBack, {
        documentType: 0,
        document: { documentId: quoteId },
      });
    },
    [alertFault, alertFaultUpdate, callApi, navigate],
  );

  const readCart = useCallback(async (): Promise<CartQuote | undefined> => {
    let cart: CartQuote | undefined;
    const callBack = (res) => {
      if (!res?.success) {
        cart = undefined;
        return;
      }
      const quote = res?.data?.succ ? res?.data?.result : null;
      cart = quote?.id ? { id: String(quote.id) } : null;
    };
    try {
      await getIfCartExistApi(callApi, callBack, { documentType: 0 }, false);
    } catch (_error) {
      cart = undefined;
    }
    return cart;
  }, [callApi]);

  const requestOpenQuote = useCallback(
    async (quoteId: string) => {
      if (!quoteId) return;
      const cart = await readCart();
      const decision =
        cart === undefined
          ? userQuoteIfExist
            ? "confirm"
            : "update"
          : decideQuoteOpen(quoteId, cart);

      if (decision === "navigate") {
        navigate("/quote");
        return;
      }
      if (decision === "confirm") {
        setPendingQuoteId(quoteId);
        setOpenModal(true);
        return;
      }
      await applyOpen(quoteId);
    },
    [applyOpen, navigate, readCart, userQuoteIfExist],
  );

  const confirmOpenQuote = useCallback(() => {
    const quoteId = pendingQuoteId;
    setOpenModal(false);
    setPendingQuoteId(null);
    if (quoteId) void applyOpen(quoteId);
  }, [applyOpen, pendingQuoteId]);

  const closeOpenQuoteModal = useCallback(() => {
    setOpenModal(false);
    setPendingQuoteId(null);
  }, []);

  const quoteOpenModal = (
    <GoMakeDeleteModal
      modalType={ModalType.WARNING}
      title={t("sales.quote.titleModal")}
      subTitle={t("sales.quote.subTitleModal")}
      yesBtn={t("sales.quote.changeStatus")}
      openModal={openModal}
      onClose={closeOpenQuoteModal}
      onClickDelete={confirmOpenQuote}
    />
  );

  return { requestOpenQuote, quoteOpenModal };
}
